import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/authSlice";
import { clearLicenseExpired } from "../../store/licenseSlice";

const SIDEBAR_STORAGE_KEY = "admin-sidebar-collapsed";
const EXPANDED_SIDEBAR_WIDTH = 240;
const COLLAPSED_SIDEBAR_WIDTH = 104;
const HEADER_HEIGHT_CLASS = "h-16";

const NavIcon = ({ children }) => (
  <span className="flex h-5 w-5 shrink-0 items-center justify-center">{children}</span>
);

const BusinessesIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
    />
  </svg>
);

const PlusIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const MenuIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M4 6h16M4 10h16M4 14h16M4 18h16"
    />
  </svg>
);

const AddOnsIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4"
    />
  </svg>
);

const CampaignIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8V6m0 10v2m9-6a9 9 0 11-18 0 9 9 0 0118 0z"
    />
  </svg>
);

const CalendarIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10m-11 9h12a2 2 0 002-2V7a2 2 0 00-2-2H6a2 2 0 00-2 2v11a2 2 0 002 2z"
    />
  </svg>
);

const WarningIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
    />
  </svg>
);

const ProteinIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"
    />
  </svg>
);

const ReportingIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 17v-6m4 6V7m4 10v-3M5 21h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v14a2 2 0 002 2z"
    />
  </svg>
);

const ListIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h10M4 18h7" />
  </svg>
);

const SettingsIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
    />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ClipboardIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
    />
  </svg>
);

const LogoutIcon = () => (
  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      strokeWidth={2}
      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
    />
  </svg>
);

const DoubleChevronLeft = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M13.5 17l-5-5 5-5" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M18.5 17l-5-5 5-5" />
  </svg>
);

const DoubleChevronRight = () => (
  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M10.5 7l5 5-5 5" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M5.5 7l5 5-5 5" />
  </svg>
);

const ChevronDownIcon = ({ open = false }) => (
  <svg
    className={["h-4 w-4 transition-transform duration-200", open ? "rotate-180" : ""].join(" ")}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
  >
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.2} d="M6 9l6 6 6-6" />
  </svg>
);

const SidebarNavLink = ({
  item,
  collapsed,
  disabled = false,
  isActive,
  isRoutePrefix = false,
  className = "",
}) => {
  const active = isRoutePrefix ? isActive(item.path, true) : isActive(item.path);
  const baseClass =
    "group relative flex items-center rounded-2xl text-sm font-medium transition-all duration-300";
  const activeClass = "bg-slate-100 text-slate-900 shadow-[0_12px_24px_rgba(15,23,42,0.10)]";
  const inactiveClass = "text-slate-600 hover:bg-slate-50 hover:text-slate-900";
  const disabledClass = disabled ? "pointer-events-none cursor-not-allowed opacity-50" : "";

  return (
    <Link
      to={item.path}
      aria-disabled={disabled}
      onClick={(event) => {
        if (disabled) {
          event.preventDefault();
        }
      }}
      title={collapsed ? item.label : item.title}
      className={[
        baseClass,
        active ? activeClass : inactiveClass,
        collapsed
          ? "mx-auto h-14 w-14 justify-center px-0"
          : item.multiline
            ? "min-h-[3.5rem] gap-3 px-4 py-3"
            : "h-12 gap-3 px-4",
        disabledClass,
        className,
      ].join(" ")}
    >
      <NavIcon>{item.icon}</NavIcon>
      {!collapsed && (
        <span className={item.multiline ? "max-w-[9rem] whitespace-normal break-words leading-5" : "truncate"}>
          {item.label}
        </span>
      )}
      {collapsed && (
        <span className="pointer-events-none absolute left-[calc(100%+14px)] top-1/2 z-50 -translate-y-1/2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 opacity-0 shadow-[0_14px_28px_rgba(15,23,42,0.14)] transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-100">
          {item.label}
        </span>
      )}
    </Link>
  );
};

const SidebarNavGroup = ({
  item,
  collapsed,
  isActive,
  isOpen,
  onToggle,
}) => {
  const groupActive = isActive(item.path, true);
  const baseClass =
    "group relative flex w-full items-center rounded-2xl text-sm font-medium transition-all duration-300";
  const activeClass =
    "bg-slate-100 text-slate-900 shadow-[0_12px_24px_rgba(15,23,42,0.10)]";
  const inactiveClass =
    "text-slate-600 hover:bg-slate-50 hover:text-slate-900";

  return (
    <div className={collapsed ? "space-y-0" : "space-y-2"}>
      <button
        type="button"
        onClick={onToggle}
        title={collapsed ? item.label : undefined}
        className={[
          baseClass,
          groupActive ? activeClass : inactiveClass,
          collapsed
            ? "mx-auto h-14 w-14 justify-center px-0"
            : item.multiline
              ? "min-h-[3.5rem] gap-3 px-4 py-3"
              : "h-12 gap-3 px-4",
        ].join(" ")}
      >
        <NavIcon>{item.icon}</NavIcon>
        {!collapsed && (
          <>
            <span
              className={
                item.multiline
                  ? "max-w-[9rem] whitespace-normal break-words text-left leading-5"
                  : "truncate"
              }
            >
              {item.label}
            </span>
            <span className="ml-auto text-slate-400">
              <ChevronDownIcon open={isOpen} />
            </span>
          </>
        )}
        {collapsed && (
          <span className="pointer-events-none absolute left-[calc(100%+14px)] top-1/2 z-50 -translate-y-1/2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 opacity-0 shadow-[0_14px_28px_rgba(15,23,42,0.14)] transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-100">
            {item.label}
          </span>
        )}
      </button>

      {!collapsed && isOpen && (
        <div className="relative ml-6 space-y-1 border-l border-slate-200/90 pl-4">
          {item.children.map((child) => (
            <SidebarNavLink
              key={child.path}
              item={child}
              collapsed={false}
              disabled={child.disabled}
              isActive={isActive}
              isRoutePrefix={child.isPrefix}
              className="h-10 rounded-xl pr-3 text-sm shadow-none before:absolute before:-left-[1.1rem] before:top-1/2 before:h-px before:w-3 before:-translate-y-1/2 before:bg-slate-200"
            />
          ))}
        </div>
      )}

      {collapsed && isOpen && (
        <div className="relative mt-2 flex flex-col items-center gap-2 pt-2">
          <span className="pointer-events-none absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-slate-200/90" />
          {item.children.map((child) => (
            <div key={child.path} className="relative z-10">
              <span className="pointer-events-none absolute left-1/2 top-1/2 h-px w-3 -translate-x-full -translate-y-1/2 bg-slate-200/90" />
              <SidebarNavLink
                item={child}
                collapsed
                disabled={child.disabled}
                isActive={isActive}
                isRoutePrefix={child.isPrefix}
                className="h-11 w-11 rounded-xl border border-transparent bg-white/95 shadow-none hover:border-slate-200"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

const MainLayout = ({ children }) => {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => {
    try {
      return localStorage.getItem(SIDEBAR_STORAGE_KEY) === "true";
    } catch {
      return false;
    }
  });
  const [openGroups, setOpenGroups] = useState({});
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const mainContentRef = useRef(null);

  const user = useSelector((state) => state.auth.user);
  const licenseState = useSelector((state) => state.license);
  const isSuperAdmin = user?.isPlatformAdmin === true;
  const isLicenseExpired = licenseState?.isExpired === true;
  const licenseMessage =
    licenseState?.message || "License expired. Please renew to continue.";
  const renewalBusinessId = licenseState?.businessId || user?.businessId || null;
  const renewalPath = isSuperAdmin
    ? renewalBusinessId
      ? `/businesses/${renewalBusinessId}/licenses`
      : "/businesses"
    : null;

  const sidebarWidth = isSidebarCollapsed ? COLLAPSED_SIDEBAR_WIDTH : EXPANDED_SIDEBAR_WIDTH;
  const isBusinessRoute = location.pathname.startsWith("/businesses");
  const isActive = (path, prefix = false) =>
    prefix ? location.pathname.startsWith(path) : location.pathname === path;

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_STORAGE_KEY, String(isSidebarCollapsed));
    } catch {
      // Ignore storage failures.
    }
  }, [isSidebarCollapsed]);

  useEffect(() => {
    mainContentRef.current?.scrollTo({ top: 0, behavior: "auto" });
  }, [location.pathname, location.search]);

  useEffect(() => {
    if (location.pathname.startsWith("/menu/reservations")) {
      setOpenGroups((prev) => ({ ...prev, reservations: true }));
    }
  }, [location.pathname]);

  const handleLogout = () => {
    dispatch(logout());
    dispatch(clearLicenseExpired());
    navigate("/login");
  };

  const businessSections = [
    {
      title: "Businesses",
      items: [
        { path: "/businesses", label: "All Businesses", icon: <BusinessesIcon /> },
        { path: "/businesses/new", label: "New Business", icon: <PlusIcon /> },
      ],
    },
  ];

  const adminSections = useMemo(
    () => [
      {
        title: "Menu",
        items: [
          {
            path: "/menu/categories",
            label: "Categories",
            icon: <MenuIcon />,
          },
          {
            path: "/menu/items",
            label: "Items",
            icon: <PlusIcon />,
          },
          {
            path: "/menu/modifiers",
            label: "Add Ons",
            icon: <AddOnsIcon />,
          },
          {
            path: "/menu/upsell-campaigns",
            label: "Upsell Campaigns",
            icon: <CampaignIcon />,
          },
          {
            path: "/menu/weekly-scheduling",
            label: "Weekly Scheduling",
            icon: <CalendarIcon />,
          },
          {
            path: "/menu/reservations",
            label: "Reservations",
            icon: <CalendarIcon />,
            children: [
              {
                path: "/menu/reservations/create-slots",
                label: "Create Slots",
                icon: <PlusIcon />,
              },
              {
                path: "/menu/reservations/exceptions",
                label: "Exceptions",
                icon: <WarningIcon />,
              },
              {
                path: "/menu/reservations/settings",
                label: "Settings",
                icon: <SettingsIcon />,
              },
            ],
          },
        ],
      },
      {
        title: "Menu Attributes",
        items: [
          {
            path: "/menu/allergens",
            label: "Allergens",
            icon: <WarningIcon />,
          },
          {
            path: "/menu/protein-types",
            label: "Protein Types",
            icon: <ProteinIcon />,
          },
        ],
      },
      {
        title: "Reporting",
        items: [
          {
            path: "/reporting/completed-orders",
            label: "Completed Orders",
            icon: <ReportingIcon />,
            isPrefix: true,
          },
          {
            path: "/reporting/top-items",
            label: "Items",
            icon: <ListIcon />,
          },
          {
            path: "/reporting/cashing-up/sessions",
            label: "Cash Sessions",
            icon: <ReportingIcon />,
            isPrefix: true,
          },
          {
            path: "/reporting/cashing-up/variances",
            label: "Variances",
            icon: <ReportingIcon />,
            isPrefix: true,
          },
          // {
          //   path: "/reporting/cashing-up/paid-outs",
          //   label: "Paid Outs",
          //   icon: <ReportingIcon />,
          //   isPrefix: true,
          // },
        ],
      },
      {
        title: "Settings",
        items: [
          {
            path: "/settings/general",
            label: "General",
            icon: <SettingsIcon />,
            disabled: isLicenseExpired,
            title: isLicenseExpired
              ? "License renewal required to access settings."
              : undefined,
          },
          {
            path: "/settings/order-types",
            label: "Order Types",
            icon: <ClipboardIcon />,
            disabled: isLicenseExpired,
            title: isLicenseExpired
              ? "License renewal required to access settings."
              : undefined,
          },
        ],
      },
    ],
    [isLicenseExpired],
  );

  const visibleSections = isSuperAdmin ? (isBusinessRoute ? businessSections : []) : adminSections;

  const toggleGroup = (groupKey) => {
    setOpenGroups((prev) => ({ ...prev, [groupKey]: !prev[groupKey] }));
  };

  return (
    <div className="flex h-screen overflow-hidden bg-[var(--color-sand)] text-[var(--color-ink)]">
      <aside
        className="fixed left-0 top-0 z-40 h-full border-r border-[rgba(15,23,42,0.08)] bg-white shadow-[0_16px_40px_rgba(15,23,42,0.05)] transition-[width] duration-300"
        style={{ width: sidebarWidth }}
      >
        <div className="relative flex h-full flex-col">
          <div className={`${HEADER_HEIGHT_CLASS} flex shrink-0 items-center border-b border-[rgba(15,23,42,0.08)] px-5`}>
            {isSidebarCollapsed ? (
              <div className="flex w-full justify-center">
                <img
                  src={`${process.env.PUBLIC_URL}/logo-goggi.png`}
                  alt="Goggi logo"
                  className="h-10 w-10 rounded-2xl object-contain shadow-[0_12px_24px_rgba(15,23,42,0.10)]"
                />
              </div>
            ) : (
              <span className="text-sm font-semibold uppercase tracking-[0.2em] text-slate-500">
                Admin Portal
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={() => setIsSidebarCollapsed((prev) => !prev)}
            aria-label={isSidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}
            className="absolute right-0 top-8 z-50 flex h-9 w-9 -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-700 shadow-[0_12px_24px_rgba(15,23,42,0.14)] transition-all duration-300 hover:scale-105 hover:text-slate-900 hover:shadow-[0_16px_32px_rgba(15,23,42,0.18)]"
          >
            {isSidebarCollapsed ? <DoubleChevronRight /> : <DoubleChevronLeft />}
          </button>

          <nav
            className={[
              "flex-1 overflow-y-auto overflow-x-hidden py-5",
              isSidebarCollapsed ? "px-4" : "px-3.5",
            ].join(" ")}
          >
            <div className="space-y-6">
              {visibleSections.map((section) => (
                <div key={section.title} className="space-y-2">
                  {!isSidebarCollapsed && (
                    <div className="px-3 text-xs font-semibold uppercase tracking-wider text-slate-400">
                      {section.title}
                    </div>
                  )}
                  <div className={isSidebarCollapsed ? "space-y-3" : "space-y-2"}>
                    {section.items.map((item) => {
                      if (item.children?.length) {
                        return (
                          <SidebarNavGroup
                            key={item.path}
                            item={item}
                            collapsed={isSidebarCollapsed}
                            isActive={isActive}
                            isOpen={Boolean(openGroups.reservations)}
                            onToggle={() => toggleGroup("reservations")}
                          />
                        );
                      }

                      return (
                        <SidebarNavLink
                          key={item.path}
                          item={item}
                          collapsed={isSidebarCollapsed}
                          disabled={item.disabled}
                          isActive={isActive}
                          isRoutePrefix={item.isPrefix}
                        />
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {!isSuperAdmin && isLicenseExpired && !isSidebarCollapsed && (
              <p className="mt-5 px-3 text-xs font-medium text-rose-600">
                License renewal required to access Settings.
              </p>
            )}
          </nav>

          <div
            className={[
              "shrink-0 border-t border-[rgba(15,23,42,0.08)] py-4",
              isSidebarCollapsed ? "px-4" : "px-3.5",
            ].join(" ")}
          >
            <button
              type="button"
              onClick={handleLogout}
              title={isSidebarCollapsed ? "Log out" : undefined}
              className={[
                "group relative flex items-center rounded-2xl text-sm font-medium text-slate-700 transition-all duration-300 hover:bg-slate-50 hover:text-slate-900",
                isSidebarCollapsed ? "mx-auto h-14 w-14 justify-center px-0" : "h-12 w-full gap-3 px-4",
              ].join(" ")}
            >
              <NavIcon>
                <LogoutIcon />
              </NavIcon>
              {!isSidebarCollapsed && <span>Log out</span>}
              {isSidebarCollapsed && (
                <span className="pointer-events-none absolute left-[calc(100%+14px)] top-1/2 z-50 -translate-y-1/2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 opacity-0 shadow-[0_14px_28px_rgba(15,23,42,0.14)] transition-all duration-200 group-hover:translate-x-1 group-hover:opacity-100">
                  Log out
                </span>
              )}
            </button>
          </div>
        </div>
      </aside>

      <div
        className="flex h-screen flex-1 flex-col overflow-hidden transition-[margin] duration-300"
        style={{ marginLeft: sidebarWidth }}
      >
        <div
          className={`sticky top-0 z-30 ${HEADER_HEIGHT_CLASS} border-b border-[rgba(15,23,42,0.08)] bg-white/70 backdrop-blur`}
        >
          <div className="flex h-full items-center justify-end px-6">
            {/* Store selector removed per request */}
          </div>
        </div>

        {isLicenseExpired && (
          <div className="border-b border-rose-200 bg-rose-50 px-6 py-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm font-medium text-rose-700">{licenseMessage}</p>
              {renewalPath ? (
                <Link
                  to={renewalPath}
                  className="rounded-lg border border-rose-300 bg-white px-3 py-1.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100"
                >
                  Renew License
                </Link>
              ) : (
                <p className="text-xs font-medium text-rose-700">
                  Contact your platform admin to renew the license.
                </p>
              )}
            </div>
          </div>
        )}

        <div
          ref={mainContentRef}
          id="main-content-scroll"
          className="flex-1 overflow-y-auto px-6 pb-10 pt-0"
        >
          {children}
        </div>
      </div>
    </div>
  );
};

export default MainLayout;
